/**
 * paden.js — de publieke pagina's in beide talen.
 *
 * Elke publieke pagina heeft een Nederlandse URL en een Engelse onder /en/...
 * Dit is de enige plek waar die koppeling staat: de bovenbalk, de footer, de
 * taalwissel, de hreflang-links (SEO) en de routes in App.js gebruiken hem.
 * site.test.js controleert dat elke Engelse URL een route in App.js heeft en
 * in reactSnap.include staat (behalve de inlogpagina, zie NIET_VOORRENDEREN).
 *
 * De werken-bij-sectie heeft zijn eigen PADEN in content/vacatures/teksten.js;
 * de ingangen daarvan staan hier ook (werkenBij), zodat de bovenbalk erheen kan.
 */

export const TALEN = ['nl', 'en'];

export const PAGINAS = {
  home: { nl: '/', en: '/en' },
  overOns: { nl: '/over-ons', en: '/en/about' },
  kennisbank: { nl: '/kennisbank', en: '/en/knowledge-base' },
  werkenBij: { nl: '/werken-bij', en: '/en/careers' },
  openSollicitatie: { nl: '/werken-bij/open-sollicitatie', en: '/en/careers/open-application' },
  contact: { nl: '/contact', en: '/en/contact' },
  demo: { nl: '/demo', en: '/en/demo' },
  hoeWerktHet: { nl: '/hoe-werkt-het', en: '/en/how-it-works' },
  faq: { nl: '/veelgestelde-vragen', en: '/en/faq' },
  download: { nl: '/download', en: '/en/download' },
  privacy: { nl: '/privacy', en: '/en/privacy' },
  voorwaarden: { nl: '/voorwaarden', en: '/en/terms' },
  subverwerkers: { nl: '/subverwerkers', en: '/en/subprocessors' },
  digitaalToetsen: { nl: '/digitaal-toetsen', en: '/en/digital-assessment' },
  login: { nl: '/login', en: '/en/login' },
};

// Oude adressen die doorsturen (zoals /offerte -> /demo).
export const DOORSTUREN = {
  '/en/quote': PAGINAS.demo.en,
};

// Vakpagina's: de Nederlandse slug staat in src/data/vakpaginas.js.
export const VAK_SLUGS = {
  wiskunde: 'mathematics',
  talen: 'languages',
  'geschiedenis-en-aardrijkskunde': 'history-and-geography',
};

const geldigeTaal = (lang) => (TALEN.includes(lang) ? lang : 'nl');

/** Het pad van een pagina in een taal: pad('contact', 'en') -> '/en/contact'. */
export function pad(sleutel, lang = 'nl') {
  const p = PAGINAS[sleutel];
  if (!p) throw new Error(`Onbekende pagina: ${sleutel}`);
  return p[geldigeTaal(lang)];
}

/** { nl, en } van een pagina, voor de taalwissel en hreflang. */
export function alternatief(sleutel) {
  const p = PAGINAS[sleutel];
  if (!p) throw new Error(`Onbekende pagina: ${sleutel}`);
  return { nl: p.nl, en: p.en };
}

/** Vakpagina: vakPad('wiskunde', 'en') -> '/en/digital-assessment/mathematics'. */
export function vakPad(nlSlug, lang = 'nl') {
  if (geldigeTaal(lang) === 'en') return `${PAGINAS.digitaalToetsen.en}/${VAK_SLUGS[nlSlug] || nlSlug}`;
  return `${PAGINAS.digitaalToetsen.nl}/${nlSlug}`;
}

/** De Nederlandse slug bij een slug uit een Engelse URL (of de slug zelf). */
export function nlVakSlug(slug) {
  const gevonden = Object.entries(VAK_SLUGS).find(([, en]) => en === slug);
  return gevonden ? gevonden[0] : slug;
}

/** Kennisbankartikel: de tekst blijft Nederlands, de omlijsting volgt de taal. */
export function artikelPad(slug, lang = 'nl') {
  return `${pad('kennisbank', lang)}/${slug}`;
}

/** Auteurspagina in de kennisbank. */
export function auteurPad(slug, lang = 'nl') {
  return geldigeTaal(lang) === 'en'
    ? `${PAGINAS.kennisbank.en}/author/${slug}`
    : `${PAGINAS.kennisbank.nl}/auteur/${slug}`;
}

/** Alle vaste Engelse pagina's (zonder artikelen en vacatures). */
export function engelsePaden() {
  return [
    ...Object.values(PAGINAS).map((p) => p.en),
    ...Object.keys(VAK_SLUGS).map((s) => vakPad(s, 'en')),
  ];
}
